/**
 * Client API pour CareWash GO
 * Avec CapacitorHttp.enabled: true, les appels fetch() sont automatiquement
 * interceptés et exécutés nativement sur iOS/Android (contourne CORS)
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import axios from 'axios';

// Détecte si on est sur une plateforme native
export const IS_NATIVE = Capacitor.isNativePlatform();

// URL du backend - PRODUCTION
const API_BASE_URL = 'https://carewashgo.app';

// Clés de stockage
const USER_ID_KEY = 'carewash_user_id';

/**
 * Stocker le user_id
 */
export const saveAuthData = async (userId) => {
  console.log('📱 saveAuthData called with userId:', userId);
  
  // Stocker globalement comme backup
  if (typeof window !== 'undefined') {
    window.__CAREWASH_USER_ID__ = userId;
  }
  
  if (IS_NATIVE) {
    await Preferences.set({ key: USER_ID_KEY, value: userId });
    console.log('📱 saveAuthData: Saved to Preferences');
    // Aussi sauvegarder dans localStorage comme backup
    localStorage.setItem(USER_ID_KEY, userId);
    console.log('📱 saveAuthData: Also saved to localStorage as backup');
  } else {
    localStorage.setItem(USER_ID_KEY, userId);
    console.log('📱 saveAuthData: Saved to localStorage');
  }
};

/**
 * Récupérer le user_id
 */
export const getAuthData = async () => {
  let userId = null;
  
  if (IS_NATIVE) {
    const result = await Preferences.get({ key: USER_ID_KEY });
    userId = result.value;
    console.log('📱 getAuthData from Preferences:', userId);
    
    // Fallback to localStorage if Preferences is empty
    if (!userId) {
      userId = localStorage.getItem(USER_ID_KEY);
      console.log('📱 getAuthData fallback to localStorage:', userId);
    }
  } else {
    userId = localStorage.getItem(USER_ID_KEY);
    console.log('📱 getAuthData from localStorage:', userId);
  }
  
  // Fallback to global variable
  if (!userId && typeof window !== 'undefined' && window.__CAREWASH_USER_ID__) {
    userId = window.__CAREWASH_USER_ID__;
    console.log('📱 getAuthData fallback to global:', userId);
  }
  
  return { userId };
};

/**
 * Supprimer les données d'auth (logout)
 */
export const clearAuthData = async () => {
  if (IS_NATIVE) {
    await Preferences.remove({ key: USER_ID_KEY });
  } else {
    localStorage.removeItem(USER_ID_KEY);
  }
  // Also clear On-Demand access cache on logout
  localStorage.removeItem('ondemand_access');
};

/**
 * Faire une requête HTTP native via fetch
 * Capacitor 6 avec CapacitorHttp.enabled intercepte automatiquement fetch()
 */
const nativeRequest = async (config) => {
  try {
    let { userId } = await getAuthData();
    
    console.log('📱 nativeRequest - userId from Preferences:', userId);
    console.log('📱 nativeRequest - config:', config.method, config.url);
    
    // Si pas de userId dans Preferences, essayer localStorage comme fallback
    if (!userId) {
      userId = localStorage.getItem('carewash_user_id');
      console.log('📱 nativeRequest - Fallback to localStorage:', userId);
    }
    
    // Dernier recours: essayer de récupérer depuis le contexte utilisateur global
    if (!userId && window.__CAREWASH_USER_ID__) {
      userId = window.__CAREWASH_USER_ID__;
      console.log('📱 nativeRequest - Fallback to global:', userId);
    }
    
    if (!userId) {
      console.warn('📱 nativeRequest - WARNING: No userId found anywhere! Requests will fail auth.');
    }
    
    const url = config.url.startsWith('http') 
      ? config.url 
      : `${API_BASE_URL}${config.url}`;
    
    // Détecter si c'est un FormData (upload de fichier)
    const isFormData = config.data instanceof FormData;
    
    const headers = {
      'Accept': 'application/json',
      ...(config.headers || {}),
    };
    
    // Ne pas définir Content-Type pour FormData - le navigateur le fait automatiquement avec le boundary
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    if (userId) {
      headers['X-User-Id'] = userId;
    }
    
    const fetchOptions = {
      method: config.method || 'GET',
      headers,
    };
    
    if (config.data && ['POST', 'PUT', 'PATCH'].includes(config.method)) {
      // Pour FormData, envoyer tel quel; sinon, JSON stringify
      fetchOptions.body = isFormData ? config.data : JSON.stringify(config.data);
    }
    
    let finalUrl = url;
    if (config.params) {
      const queryString = new URLSearchParams(config.params).toString();
      finalUrl = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
    }
    
    console.log('📱 nativeRequest - Fetching:', finalUrl);
    console.log('📱 nativeRequest - Method:', config.method);
    console.log('📱 nativeRequest - Headers:', JSON.stringify(headers));
    
    // Timeout de 30 secondes pour éviter les requêtes bloquées
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    fetchOptions.signal = controller.signal;
    
    let response;
    try {
      response = await fetch(finalUrl, fetchOptions);
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      console.error('📱 Fetch error:', fetchError);
      throw fetchError;
    }
    
    console.log('📱 nativeRequest - Response status:', response.status);
    
    let responseData;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      const text = await response.text();
      try {
        responseData = JSON.parse(text);
      } catch (e) {
        responseData = { message: text };
      }
    }
    
    // Si le status HTTP indique une erreur, rejeter la promesse (compatible axios)
    if (!response.ok) {
      const error = new Error(responseData?.detail || responseData?.message || `HTTP ${response.status}`);
      error.response = {
        data: responseData,
        status: response.status,
        headers: Object.fromEntries(response.headers.entries()),
      };
      throw error;
    }
    
    return {
      data: responseData,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
    };
  } catch (error) {
    // Si c'est une erreur HTTP qu'on a créée, la propager
    if (error.response) {
      throw error;
    }
    
    console.error('📱 Native request error:', error);
    console.error('📱 Error name:', error.name);
    console.error('📱 Error message:', error.message);
    console.error('📱 Error stack:', error.stack);
    
    // Déterminer le type d'erreur pour un message plus précis
    let errorMessage = 'Erreur de connexion. Vérifiez votre connexion internet.';
    let errorDetail = error.message || 'Network error';
    
    // Vérifier si c'est une erreur CORS ou de réseau spécifique
    if (error.message?.includes('CORS') || error.message?.includes('cross-origin')) {
      errorMessage = 'Erreur de sécurité. Veuillez réessayer.';
      errorDetail = 'CORS error';
    } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
      errorMessage = 'Impossible de joindre le serveur. Vérifiez votre connexion.';
      errorDetail = 'Network unreachable';
    } else if (error.message?.includes('timeout') || error.message?.includes('Timeout')) {
      errorMessage = 'Le serveur ne répond pas. Réessayez plus tard.';
      errorDetail = 'Request timeout';
    }
    
    // Erreur réseau - créer un objet compatible axios
    const networkError = new Error(errorMessage);
    networkError.response = {
      data: { 
        error: errorDetail, 
        detail: errorMessage,
        success: false,
        originalError: error.message
      },
      status: 0,
      headers: {},
    };
    throw networkError;
  }
};

/**
 * Client API compatible axios
 * Sur native: utilise fetch (intercepté par CapacitorHttp)
 * Sur web: utilise axios normal
 */
const createApiClient = () => {
  if (IS_NATIVE) {
    return {
      get: (url, config = {}) => nativeRequest({ ...config, url, method: 'GET' }),
      post: (url, data, config = {}) => nativeRequest({ ...config, url, method: 'POST', data }),
      put: (url, data, config = {}) => nativeRequest({ ...config, url, method: 'PUT', data }),
      delete: (url, config = {}) => nativeRequest({ ...config, url, method: 'DELETE' }),
      patch: (url, data, config = {}) => nativeRequest({ ...config, url, method: 'PATCH', data }),
    };
  }
  
  // Sur web, utiliser axios avec les cookies
  const instance = axios.create({
    baseURL: '',
    withCredentials: true,
  });
  
  return instance;
};

export const apiClient = createApiClient();
export const API_URL = API_BASE_URL;

/**
 * Normalise la réponse pour un format cohérent
 * Ajoute response.ok basé sur le status HTTP
 */
const normalizeResponse = (response) => {
  return {
    ...response,
    ok: response.status >= 200 && response.status < 300,
    data: response.data
  };
};

// Wrapper pour gérer les erreurs de façon uniforme
const safeRequest = async (requestFn) => {
  try {
    const response = await requestFn();
    return normalizeResponse(response);
  } catch (error) {
    // Si l'erreur a une response (erreur HTTP), normaliser
    if (error.response) {
      return {
        ok: false,
        status: error.response.status,
        data: error.response.data
      };
    }
    // Erreur réseau - retourner un objet normalisé
    return {
      ok: false,
      status: 0,
      data: { 
        detail: error.message || 'Erreur de connexion',
        error: 'network_error'
      }
    };
  }
};

// Export pour compatibilité avec l'ancien code (avec normalisation)
export const httpGet = async (endpoint, options) => {
  return safeRequest(() => apiClient.get(endpoint, options));
};

export const httpPost = async (endpoint, data, options) => {
  return safeRequest(() => apiClient.post(endpoint, data, options));
};

export const httpPut = async (endpoint, data, options) => {
  return safeRequest(() => apiClient.put(endpoint, data, options));
};

export const httpDelete = async (endpoint, options) => {
  return safeRequest(() => apiClient.delete(endpoint, options));
};

export const httpPatch = async (endpoint, data, options) => {
  return safeRequest(() => apiClient.patch(endpoint, data, options));
};
